/**
 * Review comments — shared types + small helpers for the editor's Comment
 * mode, the Change requests inbox and the ReviewPanel. See docs/REVIEW.md.
 *
 * The anchor is built by the site's PreviewBridge (apps/site PreviewBridge
 * .astro) and validated by apps/api/src/routes/review.ts AnchorSchema — keep
 * the three in step.
 */

import type { PreviewWidth } from '~/components/PagePreview';

export type ReviewViewport = PreviewWidth;
export type ReviewKind = 'change' | 'dev';
export type ReviewStatus = 'open' | 'resolved';

/** Where a pin points: a record / section / field plus the click's offset
 * inside it. x/y (page px) are the fallback when the element is gone. */
export interface ReviewAnchor {
  v: 1;
  section: { id: string; index: number; type: string } | null;
  chrome: string | null;
  rec: string | null;
  f: string | null;
  sel: string | null;
  text: string;
  /** Offset within the anchored element (0..1). */
  ox: number;
  oy: number;
  /** Offset within the base box (record / section / chrome). */
  bx: number;
  by: number;
  x: number;
  y: number;
  /** Page width the pin was dropped at (px). */
  pw: number;
}

export interface ReviewComment {
  id: string;
  thread_id: string;
  kind: 'comment' | 'shipped';
  body: string;
  author_id: string;
  author_email: string;
  author_role: string;
  created_at: string;
}

export interface ReviewThread {
  id: string;
  page: string;
  kind: ReviewKind;
  number: number;
  status: ReviewStatus;
  viewport: ReviewViewport;
  viewport_width: number | null;
  anchor: ReviewAnchor | null;
  created_by: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_by_email: string | null;
  comments: ReviewComment[];
}

/** The draft pin between a click and "Add comment". */
export interface ReviewDraft {
  anchor: ReviewAnchor;
  viewport: ReviewViewport;
  viewportWidth: number;
}

export const VIEWPORT_LABEL: Record<ReviewViewport, string> = {
  mobile: 'Phone',
  tablet: 'Tablet',
  desktop: 'Desktop',
  wide: 'Wide',
};

/** The number printed on a pin: 3 for a change request, D3 for a dev note. */
export function pinLabel(t: Pick<ReviewThread, 'kind' | 'number'>): string {
  return t.kind === 'dev' ? `D${t.number}` : String(t.number);
}

/** "Home", "Glp1" — only slugs are stored; the inbox has no page docs. */
export function pageLabel(slug: string): string {
  return slug === 'home' ? 'Home' : slug.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Author display. `claude` is the sentinel the dev tooling writes. */
export function authorName(email: string, myEmail?: string | null): string {
  if (email === 'claude') return 'Claude';
  if (myEmail && email.toLowerCase() === myEmail.toLowerCase()) return 'You';
  const local = email.split('@')[0] ?? email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export function ago(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 8 ? `${d}d ago` : new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Deep link into the editor at a pin (what the digest email links to). */
export function reviewHref(t: Pick<ReviewThread, 'page' | 'id'>): string {
  return `/pages?slug=${encodeURIComponent(t.page)}&review=${t.id}`;
}
